import React from "react";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { BRAND } from "src/theme/brandTokens";
import { parseProximosPasos } from "src/utils/parseProximosPasos";

const padNumero = (n) => String(n).padStart(2, "0");

/**
 * Renderiza la seccion "Proximos Pasos" del portal cliente.
 * - Si el texto sigue el formato canonico (>=2 bloques separados por linea
 *   en blanco), muestra una lista numerada (titulo + descripcion por paso).
 * - Si el vendedor escribio prosa libre o un solo bloque, cae a un render
 *   de parrafos que replica la logica visual de `ParrafosV2`, para evitar
 *   romper visualmente la seccion.
 *
 * La logica de parseo vive en `src/utils/parseProximosPasos` y mantiene
 * paridad con el parser equivalente del backend (PDF).
 */
const ProximosPasosListaV2 = ({ texto }) => {
  const { estructurado, bloques, textoBruto } = parseProximosPasos(texto);

  if (!estructurado) {
    const parrafos = (textoBruto || "")
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parrafos.length === 0) return null;
    return (
      <>
        {parrafos.map((parrafo, i) => (
          <Box
            component="p"
            key={i}
            sx={{
              m: 0,
              mb: "12pt",
              textIndent: 0,
              fontFamily: BRAND.font.family.base,
              fontSize: BRAND.font.size.body,
              color: BRAND.color.ink,
              lineHeight: BRAND.font.lineHeight.body,
              "&:last-child": { mb: 0 },
            }}
          >
            {parrafo.split("\n").map((linea, idx, arr) => (
              <React.Fragment key={idx}>
                {linea}
                {idx < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </Box>
        ))}
      </>
    );
  }

  return (
    <Box
      component="ol"
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        display: "flex",
        flexDirection: "column",
        gap: "6mm",
      }}
    >
      {bloques.map((paso, i) => (
        <Box
          component="li"
          key={i}
          sx={{
            display: "flex",
            gap: "6mm",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              flex: "0 0 auto",
              minWidth: "18mm",
              fontFamily: BRAND.font.family.base,
              fontWeight: BRAND.font.weight.bold,
              fontSize: "28pt",
              color: BRAND.color.primary,
              lineHeight: 1,
            }}
          >
            {padNumero(i + 1)}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "2mm",
            }}
          >
            <Box
              sx={{
                fontFamily: BRAND.font.family.base,
                fontWeight: BRAND.font.weight.bold,
                fontSize: BRAND.font.size.bodyLarge,
                color: BRAND.color.inkSoft,
              }}
            >
              {paso.titulo}
            </Box>
            {paso.descripcion && (
              <Box
                sx={{
                  fontFamily: BRAND.font.family.base,
                  fontWeight: BRAND.font.weight.regular,
                  fontSize: BRAND.font.size.body,
                  color: BRAND.color.grey70,
                  lineHeight: 1.5,
                }}
              >
                {paso.descripcion}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

ProximosPasosListaV2.propTypes = {
  texto: PropTypes.string,
};

ProximosPasosListaV2.defaultProps = {
  texto: "",
};

export default ProximosPasosListaV2;
